import { useRouterState, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import MobileSearchModal from "@/components/mobile-search-modal";
import { Button } from "@/components/ui/button";
import { regionByCoords } from "@/helpers/location";
import useCurrentLocation from "@/hooks/use-current-location";
import type { ExploreSearchParams } from "@/routes/explore/index";

import useSearchFilters from "../hooks/use-search-filters";
import useSelectedCategories from "../hooks/use-selected-categories";
import useSelectedSortOption from "../hooks/use-selected-sort-option";
import useSortMenuOptions from "../hooks/use-sort-menu-options";
import FiltersModal from "./filters-modal";
import { FiltersIcon, SearchIcon, SortIcon } from "./icons";
import SortMenu from "./sort-menu";

export default function MobileSearchHeader() {
  const [locationLabel, setLocationLabel] = useState("Finding your location…");

  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const asPath = useRouterState({ select: (s) => s.location.href });

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { location } = useCurrentLocation();
  const { includeLabel } = useSelectedCategories();
  const [selectedSortOption] = useSelectedSortOption();
  const sortMenuOptions = useSortMenuOptions();
  const { appliedFiltersCount, filterProps } = useSearchFilters();

  const filtersCountIcon = useMemo(
    () => (appliedFiltersCount > 0 ? <span>{appliedFiltersCount}</span> : null),
    [appliedFiltersCount]
  );

  useEffect(() => {
    const [path] = asPath.split("?");
    const segments = path.slice(1).split("/");

    if (segments[0] === "explore") {
      if (segments.length > 1) {
        setLocationLabel(`Near ${decodeURIComponent(segments[1])}`);
      } else if (typeof search.location === "string") {
        (async () => {
          const address = await regionByCoords(search.location as string);
          setLocationLabel(`Near ${address}`);
        })();
      }
      return;
    }

    if (location.address) {
      setLocationLabel(`Near ${location.address}`);
      return;
    }

    setLocationLabel("Unknown location");
  }, [asPath, location, search]);

  return (
    <>
      <div className="mr-3.5 flex h-10 grow items-center gap-1.5 rounded-[20px] bg-white/90 pr-[5px] pl-0.5">
        {/** biome-ignore lint/a11y/noStaticElementInteractions: sometimes you can */}
        {/** biome-ignore lint/a11y/useKeyWithClickEvents: sometimes you can */}
        {/** biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy */}
        <div
          className="flex grow cursor-pointer items-center"
          onClick={() => setIsSearchOpen(true)}
        >
          <SearchIcon className="mx-3" />
          <div className="flex flex-col justify-start -gap-0.5">
            <p className="line-clamp-1 text-12/[1.5em] text-black">
              {search.query || includeLabel}
            </p>
            <p className="line-clamp-1 text-10/[1.5em] text-medium-gray">
              {locationLabel}
            </p>
          </div>
        </div>
        <div className="relative">
          <Button
            aria-label="Filters"
            className="flex size-7.5 items-center justify-center rounded-full border border-black/20 bg-white"
            onClick={() => setIsFiltersOpen(true)}
          >
            <FiltersIcon />
          </Button>
          {!!filtersCountIcon && (
            <div className="absolute -top-[3px] -right-[3px] flex size-3.5 items-center justify-center rounded-full border border-white-smoke border-solid bg-barbie-pink text-10">
              {filtersCountIcon}
            </div>
          )}
        </div>
        <div className="relative">
          <Button
            aria-label="Sort"
            className="flex size-7.5 items-center justify-center rounded-full border border-black/20 bg-white"
            onClick={() => setIsSortOpen(true)}
          >
            <SortIcon />
          </Button>
          {!!sortMenuOptions[selectedSortOption]?.icon && (
            <div className="absolute -top-[3px] -right-[3px] flex size-3.5 items-center justify-center rounded-full border border-white-smoke border-solid bg-white *:h-auto *:max-h-2.5 *:w-auto *:max-w-2.5">
              {sortMenuOptions[selectedSortOption].icon}
            </div>
          )}
        </div>
      </div>
      <SortMenu onOpenChange={setIsSortOpen} open={isSortOpen} />
      <MobileSearchModal
        onOpenChange={setIsSearchOpen}
        open={isSearchOpen}
        searchFormProps={{ withFocus: true }}
      />
      <FiltersModal
        onOpenChange={setIsFiltersOpen}
        open={isFiltersOpen}
        {...filterProps}
      />
    </>
  );
}
